import { useParams } from 'react-router-dom';
import React, { useEffect, useState } from 'react';
import { getOfferById, Offer } from 'app/console-api';
import { SkeletonLoader } from 'app/components/common/skeleton-loader/skeleton-loader';
import { FlexColumn, SvgIcon } from '@make-software/cspr-design';
import styled from 'styled-components';
import { OfferDetailsData } from 'app/components/management/apps/offers/view/offer-details-data';
import { HttpError } from 'app/components/utils/http-error';
import { AUTH_TOKEN, customErrorMessage, statusCode } from 'app/constants';
import { SIGN_IN_PATH } from 'app/route/paths';
import { ErrorResult } from 'app/organizations-api/types';
import { ErrorWindow } from 'app/components/common/error-window/error-window';
import NotFoundIcon from 'assets/logos/no-found.svg';
import { useTabsCtx } from 'app/components/management/apps/tabs-page-layout';
import {
  PageContainer,
  StyledCaption,
  ViewPageContainer
} from 'app/components/common/common-styles';

export const StyledContainer = styled(FlexColumn)(({ theme }) =>
  theme.withMedia({
    '& > * + *': {
      marginTop: [28]
    }
  })
);

const LoadingContainer = styled(StyledContainer)(() => ({
  marginTop: '20px'
}));

export const OffersViewPage = () => {
  const { appID } = useTabsCtx();
  const { id } = useParams<{ id: string }>();
  const [offer, setOffer] = useState<Offer | undefined>(undefined);
  const [unexpectedError, setUnexpectedError] = useState<ErrorResult | null>(null);

  const { setBreadcrumbs } = useTabsCtx();

  React.useEffect(() => {
    if (!offer) {
      return;
    }
    setBreadcrumbs([
      {
        label: offer.name
      }
    ]);
    return () => setBreadcrumbs([]);
  }, [offer]);

  useEffect(() => {
    const fetchOffer = async () => {
      if (!id) {
        console.error('No ID provided in the route.');
        return;
      }

      getOfferById(appID, id)
        .then((response) => {
          setOffer(response);
        })
        .catch((error: any) => {
          if (error instanceof HttpError) {
            switch (error.statusCode) {
              case statusCode.validation_error:
                setUnexpectedError({
                  code: error.statusCode.toString(),
                  message: error.message
                });
                break;
              case statusCode.authentication_error:
                localStorage.removeItem(AUTH_TOKEN);
                window.location.href = SIGN_IN_PATH;
                return;
              case statusCode.unexpected_error:
                setUnexpectedError(customErrorMessage);
                break;
              default:
                setUnexpectedError(customErrorMessage);
                break;
            }
          } else {
            setUnexpectedError(customErrorMessage);
          }
          console.error('Failed to fetch offer:', error);
        });
    };

    fetchOffer();
  }, [id]);

  if (!offer) {
    return (
      <PageContainer>
        <ViewPageContainer justify={'start'} align={'start'}>
          <LoadingContainer>
            <SkeletonLoader height={'300px'} />
          </LoadingContainer>
        </ViewPageContainer>
      </PageContainer>
    );
  }

  if (unexpectedError) {
    return (
      <ErrorWindow
        withModal
        title={unexpectedError.code}
        information={unexpectedError.message}
        icon={<SvgIcon src={NotFoundIcon} width={219} height={125} />}
      />
    );
  }

  return (
    <PageContainer>
      <ViewPageContainer>
        <StyledCaption />
        <OfferDetailsData offer={offer} />
      </ViewPageContainer>
    </PageContainer>
  );
};
